import { absolutePosition } from "./auto-fuse";
import type { Pt } from "./cable-routing";
import { GEOMETRY } from "./canvas-geometry";
import { getDefinition } from "./catalog";
import { effectiveDimensions } from "./effective";
import type { Cable, SystemNode } from "./types";

const { OBSTACLE_PADDING } = GEOMETRY;

/** A corridor along a zone's longer axis; `tidyRoute` biases the zone's cables
 *  onto it so they gather into one bundle. */
export type Trunk = {
  zoneId: string;
  /** Manhattan polyline, fed verbatim as a `tidyRoute` corridor. */
  polyline: Pt[];
  orientation: "h" | "v";
  /** Zone bbox in absolute coords. Not derivable from the polyline — detours
   *  push its range past the zone's edges. */
  bbox: Rect;
};

type Rect = { x: number; y: number; w: number; h: number };

function paddedComponentRects(nodes: SystemNode[]): Rect[] {
  const rects: Rect[] = [];
  for (const n of nodes) {
    if (n.kind !== "component") continue;
    const def = getDefinition(n.definitionId, n.customDefinition);
    if (!def) continue;
    const abs = absolutePosition(n, nodes);
    const dims = effectiveDimensions(def, n);
    rects.push({
      x: abs.x - OBSTACLE_PADDING,
      y: abs.y - OBSTACLE_PADDING,
      w: dims.width + 2 * OBSTACLE_PADDING,
      h: dims.height + 2 * OBSTACLE_PADDING,
    });
  }
  return rects;
}

/** Detours a 2-point base polyline around the obstacles on its centreline. */
function carveTrunkAround(
  base: Pt[],
  orientation: "h" | "v",
  obstacles: Rect[],
): Pt[] {
  if (base.length !== 2) return base;
  const start = base[0];
  const end = base[1];

  if (orientation === "h") {
    const cy = start.y;
    const x0 = Math.min(start.x, end.x);
    const x1 = Math.max(start.x, end.x);
    const blocking = obstacles.filter(
      (r) =>
        r.y < cy && cy < r.y + r.h && r.x + r.w > x0 && r.x < x1,
    );
    if (blocking.length === 0) return base;
    blocking.sort((a, b) => a.x - b.x);
    const out: Pt[] = [{ x: x0, y: cy }];
    for (const r of blocking) {
      const enterX = Math.max(x0, r.x);
      const exitX = Math.min(x1, r.x + r.w);
      const aboveY = r.y;
      const belowY = r.y + r.h;
      const detourY =
        Math.abs(cy - aboveY) <= Math.abs(belowY - cy) ? aboveY : belowY;
      out.push({ x: enterX, y: cy });
      out.push({ x: enterX, y: detourY });
      out.push({ x: exitX, y: detourY });
      out.push({ x: exitX, y: cy });
    }
    out.push({ x: x1, y: cy });
    if (start.x > end.x) out.reverse();
    return out;
  }

  const cx = start.x;
  const y0 = Math.min(start.y, end.y);
  const y1 = Math.max(start.y, end.y);
  const blocking = obstacles.filter(
    (r) => r.x < cx && cx < r.x + r.w && r.y + r.h > y0 && r.y < y1,
  );
  if (blocking.length === 0) return base;
  blocking.sort((a, b) => a.y - b.y);
  const out: Pt[] = [{ x: cx, y: y0 }];
  for (const r of blocking) {
    const enterY = Math.max(y0, r.y);
    const exitY = Math.min(y1, r.y + r.h);
    const leftX = r.x;
    const rightX = r.x + r.w;
    const detourX =
      Math.abs(cx - leftX) <= Math.abs(rightX - cx) ? leftX : rightX;
    out.push({ x: cx, y: enterY });
    out.push({ x: detourX, y: enterY });
    out.push({ x: detourX, y: exitY });
    out.push({ x: cx, y: exitY });
  }
  out.push({ x: cx, y: y1 });
  if (start.y > end.y) out.reverse();
  return out;
}

export function computeZoneTrunks(nodes: SystemNode[]): Trunk[] {
  const obstacles = paddedComponentRects(nodes);
  const trunks: Trunk[] = [];
  for (const n of nodes) {
    if (n.kind !== "zone") continue;
    const abs = absolutePosition(n, nodes);
    const orientation: "h" | "v" = n.width >= n.height ? "h" : "v";
    const base: Pt[] =
      orientation === "h"
        ? [
            { x: abs.x, y: abs.y + n.height / 2 },
            { x: abs.x + n.width, y: abs.y + n.height / 2 },
          ]
        : [
            { x: abs.x + n.width / 2, y: abs.y },
            { x: abs.x + n.width / 2, y: abs.y + n.height },
          ];
    const polyline = carveTrunkAround(base, orientation, obstacles);
    const bbox: Rect = { x: abs.x, y: abs.y, w: n.width, h: n.height };
    trunks.push({ zoneId: n.id, polyline, orientation, bbox });
  }
  return trunks;
}

function manhattanBboxOverlapsRect(a: Pt, b: Pt, r: Rect): boolean {
  const x0 = Math.min(a.x, b.x);
  const x1 = Math.max(a.x, b.x);
  const y0 = Math.min(a.y, b.y);
  const y1 = Math.max(a.y, b.y);
  return x1 > r.x && x0 < r.x + r.w && y1 > r.y && y0 < r.y + r.h;
}

/** Corridors for a cable: the zone holds an endpoint, or the cable's Manhattan
 *  bbox crosses it. Scoring only rewards segments that end up parallel to the
 *  trunk, so an over-broad match costs nothing. */
export function trunkCorridorsForCable(
  cable: Cable,
  trunks: Trunk[],
  zoneAncestorsByNode: Map<string, Set<string>>,
  endpointPositions?: { from: Pt; to: Pt },
): Pt[][] {
  if (trunks.length === 0) return [];
  const fromAncestors = zoneAncestorsByNode.get(cable.fromNodeId);
  const toAncestors = zoneAncestorsByNode.get(cable.toNodeId);
  const out: Pt[][] = [];
  for (const trunk of trunks) {
    const fromIn = fromAncestors?.has(trunk.zoneId) ?? false;
    const toIn = toAncestors?.has(trunk.zoneId) ?? false;
    if (fromIn || toIn) {
      out.push(trunk.polyline);
      continue;
    }
    if (
      endpointPositions &&
      manhattanBboxOverlapsRect(
        endpointPositions.from,
        endpointPositions.to,
        trunk.bbox,
      )
    ) {
      out.push(trunk.polyline);
    }
  }
  return out;
}
